import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { LoaderCircle } from "lucide-react";
import { loadTextRegionSuggestions } from "./api";
import { normalizedPolygonClipPath, normalizedPolygonPath, regionShapeViewBox } from "./region-shapes";
import type { MangaPage, OcrRegion, TextRegionSuggestion } from "./types";

interface SuggestedTextTargetsProps {
  page: MangaPage;
  enabled: boolean;
  selectionBusy: boolean;
  recognizedRegions: OcrRegion[];
  onSelect: (page: MangaPage, region: TextRegionSuggestion) => void;
}

function intersectionOverSmallest(left: TextRegionSuggestion, right: OcrRegion): number {
  const width = Math.max(0, Math.min(left.x + left.width, right.x + right.width) - Math.max(left.x, right.x));
  const height = Math.max(0, Math.min(left.y + left.height, right.y + right.height) - Math.max(left.y, right.y));
  const intersection = width * height;
  const smallest = Math.min(left.width * left.height, right.width * right.height);
  return smallest > 0 ? intersection / smallest : 0;
}

function styleFor(region: TextRegionSuggestion): CSSProperties {
  const clipPath = normalizedPolygonClipPath(region.polygon, region);
  return {
    left: `${region.x * 100}%`,
    top: `${region.y * 100}%`,
    width: `${region.width * 100}%`,
    height: `${region.height * 100}%`,
    ...(clipPath ? { clipPath, WebkitClipPath: clipPath } : {})
  };
}

export function SuggestedTextTargets({ page, enabled, selectionBusy, recognizedRegions, onSelect }: SuggestedTextTargetsProps) {
  const [regions, setRegions] = useState<TextRegionSuggestion[]>([]);
  const [regionsPageId, setRegionsPageId] = useState<string | null>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "unavailable">("idle");
  const [focusedRegionKey, setFocusedRegionKey] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setStatus("idle");
      return;
    }
    const controller = new AbortController();
    setStatus("loading");
    loadTextRegionSuggestions(page.id, controller.signal)
      .then((suggestions) => {
        setRegions(suggestions);
        setRegionsPageId(page.id);
        setStatus("ready");
      })
      .catch((error) => {
        if (!(error instanceof DOMException && error.name === "AbortError")) setStatus("unavailable");
      });
    return () => controller.abort();
  }, [enabled, page.id]);

  const available = useMemo(
    () => (regionsPageId === page.id ? regions : []).filter((region) => !recognizedRegions.some((recognized) => intersectionOverSmallest(region, recognized) > 0.58)),
    [page.id, recognizedRegions, regions, regionsPageId]
  );
  const pathFor = (region: TextRegionSuggestion) => normalizedPolygonPath(region.polygon, page);

  if (!enabled) return null;
  return (
    <>
      {available.some((region) => pathFor(region)) && (
        <svg
          className={`region-shape-layer suggested-region-shape-layer ${selectionBusy ? "is-disabled" : ""}`}
          viewBox={regionShapeViewBox}
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {available.map((region, index) => {
            const path = pathFor(region);
            if (!path) return null;
            const key = regionKey(region, index);
            return (
              <path
                className={`suggested-region-shape ${key === focusedRegionKey ? "is-focused" : ""}`}
                d={path}
                vectorEffect="non-scaling-stroke"
                key={key}
              />
            );
          })}
        </svg>
      )}
      {available.map((region, index) => (
        <button
          className={`suggested-text-region ${pathFor(region) ? "has-polygon" : ""}`}
          style={styleFor(region)}
          type="button"
          disabled={selectionBusy}
          key={regionKey(region, index)}
          onPointerDown={(event) => event.stopPropagation()}
          onPointerEnter={() => setFocusedRegionKey(regionKey(region, index))}
          onPointerLeave={() => setFocusedRegionKey((current) => current === regionKey(region, index) ? null : current)}
          onFocus={() => setFocusedRegionKey(regionKey(region, index))}
          onBlur={() => setFocusedRegionKey((current) => current === regionKey(region, index) ? null : current)}
          onClick={(event) => {
            event.stopPropagation();
            onSelect(page, region);
          }}
          aria-label="Recognize this suggested text area"
          title="Tap to recognize"
        />
      ))}
      {status === "loading" && (
        <span className="text-target-status" role="status"><LoaderCircle className="spin" size={13} /> Finding text…</span>
      )}
    </>
  );
}

function regionKey(region: TextRegionSuggestion, index: number): string {
  return `${region.kind}:${region.x}:${region.y}:${index}`;
}
